import Filter from './Filter';
import FilterLastUsed from './FilterLastUsed';
import type { QueriesType } from '../../types';
import type { RelationsType } from '../../types-analytics';
import { featureRestoreRemovedFilters } from './featureRestoreRemovedFilters';
import { featureInactiveQueries } from './featureInactiveQueries';
import { FilterManagerIO } from './FilterManagerIO';

export const explodeAndOr = (
    queries: QueriesType
): { queries: QueriesType; __andOrSelection: string } => {
    let __andOrSelection: string = 'and'; // $and
    if (queries.$and) {
        __andOrSelection = 'and';
        queries = queries.$and[0];
    } else if (queries.$or) {
        __andOrSelection = 'or';
        queries = queries.$or[0];
    }
    return { queries, __andOrSelection };
};

export const implodeAndOr = (queries: QueriesType, andOrSelection: string): QueriesType => {
    if (Object.keys(queries).length === 0) {
        // https://er.automapa.pl/issues/41660
        return {};
    }
    if (andOrSelection === 'and') {
        return { $and: [queries] };
    } else {
        return { $or: [queries] };
    }
};

export interface localStorageFilterType {
    mainFilterData: {
        queries: QueriesType;
        related: RelationsType;
    };
    lastUsedFilterData: {
        queries: QueriesType;
        related: RelationsType;
        historyOfAddedKeys?: string[];
    };
    __inactiveQueriesKeys: string[];
    __andOrSelection: string; // and | or
}

export class FilterManager {
    private readonly mainFilter: Filter;
    private readonly lastUsedFilter: FilterLastUsed;
    private localStorageKey: string = 'filterManager';
    public featureRestoreLastRemovedFilter: featureRestoreRemovedFilters;
    public featureInactiveQueries: featureInactiveQueries;
    private filterManagerIO: FilterManagerIO;

    constructor(filterManagerIO: FilterManagerIO) {
        this.filterManagerIO = filterManagerIO;
        this.mainFilter = new Filter();
        this.lastUsedFilter = new FilterLastUsed();
        this.featureRestoreLastRemovedFilter = new featureRestoreRemovedFilters();
        this.featureInactiveQueries = new featureInactiveQueries();
    }

    setAndOrSelectionEnabled(enabled: boolean): void {
        this.mainFilter.andOrSelectionEnabled = enabled;
    }

    setLocalStorageKeyName__DEPRECATED(key: string): void {
        this.localStorageKey = key;
    }

    getFeatureRestoreLastRemovedFilter(): featureRestoreRemovedFilters {
        return this.featureRestoreLastRemovedFilter;
    }

    setFilters(filter: Filter, lastUsedFilter: FilterLastUsed): void {
        this.mainFilter.andOrSelection = filter.andOrSelection;
        this.mainFilter.setFilterQueries(filter.getFilterQueries());
        this.mainFilter.setFilterRelated(filter.getFilterRelated());
        this.lastUsedFilter.setFilterQueries(lastUsedFilter.getFilterQueries());
        this.lastUsedFilter.setFilterRelated(lastUsedFilter.getFilterRelated());
        this.lastUsedFilter.setHistoryOfAddedKeys(lastUsedFilter.getHistoryOfAddedKeys());
    }

    async loadFromLocalStorage(props: {
        loadMainFilter: boolean;
        loadLastUsedFilter: boolean;
    }): Promise<void> {
        // const storageData: string = localStorage.getItem(this.localStorageKey) ?? '{}';
        if (props.loadMainFilter) {
            this.mainFilter.setFilterQueries({});
            this.mainFilter.setFilterRelated([]);
        }
        if (props.loadLastUsedFilter) {
            this.lastUsedFilter.setFilterQueries({});
            this.lastUsedFilter.setFilterRelated([]);
            this.lastUsedFilter.setHistoryOfAddedKeys([]);
        }
        // if (storageData) {
        try {
            let {
                mainFilterData,
                lastUsedFilterData,
                __inactiveQueriesKeys,
                __andOrSelection,
            }: localStorageFilterType = await this.filterManagerIO.load();

            if (mainFilterData.queries.$and) {
                alert('FilterManager: loadFromLocalStorage: $and is not allowed here');
                throw Error('FilterManager: loadFromLocalStorage: $and is not allowed here');
            }
            if (mainFilterData.queries.$or) {
                alert('FilterManager: loadFromLocalStorage: $or is not allowed here');
                throw Error('FilterManager: loadFromLocalStorage: $or is not allowed here');
            }
            // let { mainFilterData, lastUsedFilterData, __inactiveQueriesKeys }: localStorageFilterType = JSON.parse(storageData);
            console.debug(
                'FilterManager: loadFromLocalStorage: ',
                mainFilterData,
                lastUsedFilterData,
                __inactiveQueriesKeys,
                __andOrSelection
            );
            this.featureInactiveQueries.setInActiveQueries(__inactiveQueriesKeys ?? []);
            if (props.loadMainFilter) {
                // let { queries: tempQueries, __andOrSelection} = this.explodeAndOr(mainFilterData.queries);
                this.mainFilter.andOrSelection = __andOrSelection;
                console.log('FilterManager: get andOrSelection: ', this.mainFilter.andOrSelection);
                this.mainFilter.setFilterQueries(mainFilterData.queries);
                this.mainFilter.setFilterRelated(mainFilterData.related);
            }
            if (props.loadLastUsedFilter) {
                this.lastUsedFilter.setFilterQueries(lastUsedFilterData.queries);
                this.lastUsedFilter.setFilterRelated(lastUsedFilterData.related);
                this.lastUsedFilter.setHistoryOfAddedKeys(
                    lastUsedFilterData.historyOfAddedKeys ?? []
                );
            }
        } catch (e) {
            console.warn('FilterManager: error while parsing localStorage data: ', e);
        }
        // }
    }

    async saveToLocalStorage(): Promise<void> {
        this.featureInactiveQueries.removeUnusedKeys(
            Object.keys(this.mainFilter.getFilterQueries())
        );
        let storageData: localStorageFilterType = {
            mainFilterData: {
                queries: this.mainFilter.getFilterQueries(),
                related: this.mainFilter.getFilterRelated(),
            },
            lastUsedFilterData: {
                queries: this.lastUsedFilter.getFilterQueries(),
                related: this.lastUsedFilter.getFilterRelated(),
                historyOfAddedKeys: this.lastUsedFilter.getHistoryOfAddedKeys(),
            },
            __inactiveQueriesKeys: this.featureInactiveQueries.getInActiveQueries(),
            __andOrSelection: this.mainFilter.andOrSelection,
        };
        // localStorage.setItem(this.localStorageKey, JSON.stringify(storageData));
        console.log('FilterManager: set andOrSelection: ', storageData.__andOrSelection);
        await this.filterManagerIO.save(storageData);
    }

    getMainFilter(): Filter {
        return this.mainFilter;
    }

    getMainFilterActiveQueries(): QueriesType {
        let allQueries = { ...this.mainFilter.getFilterQueries() };
        Object.keys(allQueries).forEach((key: string) => {
            if (!this.featureInactiveQueries.isActiveQuery(key)) {
                delete allQueries[key];
            }
        });
        console.log('getMainFilterActiveQueries: ', allQueries);
        return allQueries;
    }

    getAllQueries(): QueriesType {
        return JSON.parse(JSON.stringify(this.mainFilter.getFilterQueries()));
    }

    getLastUsedFilter(): FilterLastUsed {
        return this.lastUsedFilter;
    }

    clearMainFilter(): void {
        this.mainFilter.setFilterQueries({});
        this.mainFilter.setFilterRelated([]);
    }

    clearLastUsedFilter(): void {
        this.lastUsedFilter.setFilterQueries({});
        this.lastUsedFilter.setFilterRelated([]);
        this.lastUsedFilter.setHistoryOfAddedKeys([]);
    }

    getAndOrSelection(): string {
        return this.mainFilter.andOrSelection;
    }
}
